import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Printer, ArrowLeft, Globe2, Users, Map as MapIcon, Landmark } from "lucide-react";
import { fetchCountries } from "@/lib/api";
import { fmtNumber } from "@/lib/i18n";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { INTRO, REGIONS } from "@/lib/introContent";
import { OVERVIEW } from "@/lib/overviewContent";
import { fetchOverview, fmtBig, StatCard, Prose, CompareTable, Points } from "@/pages/Overview";
import RegionsMap from "@/components/RegionsMap";
import EbookChapter from "@/components/ebook/EbookChapter";
import EbookCover from "@/components/ebook/EbookCover";
import HowToUse from "@/components/ebook/HowToUse";
import Paywall from "@/components/ebook/Paywall";
import useEbookAccess from "@/lib/useEbookAccess";
import ReceiptPrompt from "@/components/ebook/ReceiptPrompt";

const shortName = (c, lang) => c.short_names?.[lang] || c.names[lang] || c.names.en;

export default function Ebook() {
  const { lang, t } = useLang();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const { data: overview } = useQuery({ queryKey: ["overview"], queryFn: fetchOverview });
  const { unlocked, buy, buying, viaPlay, unlockWith, priceLabel, bundleLabel, receiptToken, closeReceipt } = useEbookAccess("ebook_atlas");
  const product = "ebook_atlas";
  const list = useMemo(() => [...countries].sort((a, b) => shortName(a, lang).localeCompare(shortName(b, lang), lang)), [countries, lang]);
  const codeToName = useMemo(() => Object.fromEntries(countries.map((c) => [c.code, shortName(c, lang)])), [countries, lang]);

  useEffect(() => {
    document.title = `${t("ebook_title")} — ${t("app_title")}`;
    return () => { document.title = "Atlante del Mondo Arabo | أطلس العالم العربي"; };
  }, [t]);

  if (!list.length) return <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">…</div>;
  const onPrint = () => { if (!unlocked) return buy(product); trackEvent("print_ebook", { lang, countries: list.length }); window.print(); };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 print:py-0 print:px-0 print:max-w-none" data-testid="ebook">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ochre hover:underline" data-testid="ebook-back"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back")}</Link>
        <button onClick={onPrint} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-sand text-sm hover:bg-ochre transition-colors" data-testid="ebook-print-button"><Printer className="w-4 h-4" /> {unlocked ? t("print_pdf") : t("paywall_buy").replace("{price}", priceLabel)}</button>
      </div>
      <ReceiptPrompt token={receiptToken} onClose={closeReceipt} />

      <EbookCover kicker={t("app_title")} title={t("ebook_title")} subtitle={t("ebook_subtitle").replace("{n}", list.length)} note={t("ebook_cover_note")} countries={list} />
      <HowToUse variant="atlas" />
      <Toc list={list} lang={lang} t={t} />
      <Introduction countries={countries} lang={lang} t={t} />
      {(unlocked ? list : list.slice(0, 1)).map((c, i) => <EbookChapter key={c.code} country={c} index={i + 1} codeToName={codeToName} />)}
      {unlocked ? <Conclusion overview={overview} lang={lang} t={t} /> : (
        <Paywall product="ebook_atlas" buy={buy} buying={buying} viaPlay={viaPlay} unlockWith={unlockWith} remaining={list.length - 1} priceLabel={priceLabel} bundleLabel={bundleLabel}>
          <EbookChapter country={list[1]} index={2} codeToName={codeToName} />
        </Paywall>
      )}

      <footer className="mt-8 pt-3 border-t border-stoneLine text-[10px] text-muted-foreground flex justify-between">
        <span>{t("photo_wiki")} · {t("source_wb")}</span>
        <span dir="ltr">{window.location.origin}/ebook</span>
      </footer>
    </div>
  );
}

function Toc({ list, lang, t }) {
  const c = INTRO[lang] || INTRO.en;
  const o = OVERVIEW[lang] || OVERVIEW.en;
  const Row = ({ n, label, href, testid }) => (
    <li className="flex items-baseline gap-3 text-sm" data-testid={testid}>
      <span className="stat-number text-ochre w-7 text-end shrink-0">{n}</span>
      <a href={href} className="text-ink hover:text-ochre hover:underline print:no-underline flex-1 border-b border-dotted border-stoneLine pb-0.5">{label}</a>
    </li>
  );
  return (
    <nav className="mb-10 break-after-page" data-testid="ebook-toc">
      <h2 className="text-[10px] uppercase tracking-[0.18em] text-ochre font-semibold mb-4">{t("ebook_toc")}</h2>
      <ol className="space-y-2">
        <Row n="" label={`${c.kicker} — ${c.title}`} href="#ebook-intro" testid="toc-intro" />
        {list.map((k, i) => <Row key={k.code} n={i + 1} label={shortName(k, lang)} href={`#ebook-${k.code}`} testid={`toc-${k.code}`} />)}
        <Row n="" label={`${o.kicker} — ${o.title}`} href="#ebook-conclusion" testid="toc-conclusion" />
      </ol>
    </nav>
  );
}

function Introduction({ countries, lang, t }) {
  const c = INTRO[lang] || INTRO.en;
  const byCode = Object.fromEntries(countries.map((k) => [k.code, k]));
  return (
    <section id="ebook-intro" className="break-after-page scroll-mt-24 space-y-6" data-testid="ebook-intro">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold">{c.kicker}</div>
        <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink leading-tight mt-1">{c.title}</h2>
        <p className="mt-3 text-base leading-relaxed text-ink/85">{c.lead}</p>
      </div>
      <RegionsMap countries={countries} active={null} onRegion={() => {}} onCountry={() => {}} />
      <ol className="space-y-3">
        {REGIONS.map((r, i) => (
          <li key={r.id} className="flex gap-3 break-inside-avoid" data-testid={`ebook-region-${r.id}`}>
            <span className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: r.color }}>{i + 1}</span>
            <div className="min-w-0">
              <div className="font-semibold text-ink text-sm">{c.regions[r.id].name} <span className="text-muted-foreground font-normal">· {c.regions[r.id].sub}</span></div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {r.codes.map((code) => byCode[code] && (
                  <span key={code} className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border border-stoneLine bg-card">
                    <img src={byCode[code].flag_url} alt="" className="w-4 h-3 object-cover rounded-[2px] border border-stoneLine" /> {shortName(byCode[code], lang)}
                  </span>
                ))}
              </div>
            </div>
          </li>
        ))}
      </ol>
      <div>
        <h3 className="text-[10px] uppercase tracking-[0.18em] text-ochre font-semibold mb-3">{c.keys_title}</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          {c.keys.map((k) => (
            <article key={k.h} className="rounded-2xl border border-stoneLine bg-card p-4 break-inside-avoid">
              <h4 className="font-display font-bold text-base text-ink leading-snug">{k.h}</h4>
              <p className="text-sm leading-relaxed text-ink/80 mt-1.5">{k.p}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Conclusion({ overview, lang }) {
  const c = OVERVIEW[lang] || OVERVIEW.en;
  const a = overview?.arab;
  return (
    <section id="ebook-conclusion" className="break-before-page scroll-mt-24 space-y-8 pt-2" data-testid="ebook-conclusion">
      <header>
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ochre font-semibold"><Globe2 className="w-4 h-4" /> {c.kicker}</div>
        <h2 className="text-3xl sm:text-4xl font-display font-bold text-ink leading-tight mt-1">{c.title}</h2>
        <p className="mt-3 text-base leading-relaxed text-ink/85">{c.intro}</p>
      </header>
      {a && (
        <div className="grid grid-cols-2 gap-3 break-inside-avoid">
          <StatCard icon={Landmark} label={c.table.members} value={fmtNumber(a.members, lang)} sub={`+${a.territories}`} />
          <StatCard icon={Users} label={c.table.population} value={fmtBig(a.population, lang, "M")} sub={`${c.table.median_age}: ${fmtNumber(a.median_age, lang)} ${c.table.years}`} />
          <StatCard icon={MapIcon} label={c.table.area} value={fmtBig(a.area_km2, lang, "Mkm2")} />
          <StatCard icon={Globe2} label={c.table.gdp} value={fmtBig(a.gdp_usd, lang, "T")} sub={a.gdp_year ? `${a.gdp_year} · ${a.gdp_countries}/23` : undefined} />
        </div>
      )}
      <div className="rounded-2xl bg-ink text-sand p-5 break-inside-avoid print:bg-ink">
        <div className="text-[10px] uppercase tracking-[0.2em] text-ochre mb-2">{c.if_one}</div>
        <ul className="grid sm:grid-cols-3 gap-3">
          {c.ranks.map((r, i) => <li key={r} className="flex gap-3 items-start"><span className="stat-number text-3xl text-ochre leading-none">{i + 1}</span><span className="text-sm leading-snug text-sand/90">{r}</span></li>)}
        </ul>
      </div>
      <Prose {...c.sections.history} />
      <Prose {...c.sections.people} />
      <Prose {...c.sections.economy} />
      <section className="space-y-4 break-inside-avoid">
        <Prose {...c.sections.compare} />
        {overview && <CompareTable data={overview} c={c} lang={lang} />}
        {a && <p className="text-[11px] text-muted-foreground">{c.table.source.replace("{n}", a.gdp_countries)}</p>}
      </section>
      <div className="grid sm:grid-cols-2 gap-4">
        <Points {...c.sections.strengths} good />
        <Points {...c.sections.weaknesses} />
      </div>
      <section className="rounded-2xl border border-stoneLine bg-card/70 p-5 break-inside-avoid"><Prose {...c.sections.conclusion} /></section>
    </section>
  );
}
